/**
 * Content-Addressed Encrypted Asset Manager
 *
 * Implements client-side encryption, deduplication, uploading, and lazy
 * downloading/caching of media assets (images, covers, attachments)
 * decoupling binary data from Yjs CRDT document history.
 *
 * Asset URI format: yada-asset://<workspaceId>/<sha256Hex>
 */

import { uploadAsset, fetchAsset } from '@yada/api-client';
import { EnvelopeIntegrityError, deriveEpochKey, getCurrentEpoch, openEpochEnvelope, sealEpochEnvelope } from '@yada/crypto';
import { getWorkspaceSeed } from './workspaceSeedManager.js';

const ASSET_URI_PREFIX = 'yada-asset://';

export interface UploadAssetOptions {
  mimeType?: string;
}

/** Check if a given string is a yada-asset URI. */
export function isAssetUri(uri: unknown): uri is string {
  return typeof uri === 'string' && uri.startsWith(ASSET_URI_PREFIX);
}

/** Parse a yada-asset URI into its workspaceId and assetId (hash). */
export function parseAssetUri(uri: string): { workspaceId: string; assetId: string } | null {
  if (!isAssetUri(uri)) return null;
  const path = uri.slice(ASSET_URI_PREFIX.length);
  const slashIdx = path.indexOf('/');
  if (slashIdx === -1) return null;
  const workspaceId = path.slice(0, slashIdx);
  const assetId = path.slice(slashIdx + 1);
  if (!workspaceId || !assetId) return null;
  return { workspaceId, assetId };
}

/** Build a canonical yada-asset URI. */
export function buildAssetUri(workspaceId: string, assetId: string): string {
  return `${ASSET_URI_PREFIX}${workspaceId}/${assetId.toLowerCase()}`;
}

function uint8ArrayToHex(bytes: Uint8Array): string {
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * In-memory Blob URL cache to prevent decrypting or re-downloading the same asset multiple times.
 */
const resolvedBlobUrlCache = new Map<string, string>();
const inFlightResolutions = new Map<string, Promise<string>>();

export const ASSET_CACHE_DB = 'yada-asset-cache';
const ASSET_CACHE_STORE = 'decrypted_assets';

interface CachedAssetRecord {
  assetUri: string;
  blob: Blob;
  mimeType: string;
  cachedAt: number;
}

let dbPromise: Promise<IDBDatabase> | null = null;

function openAssetCacheDB(): Promise<IDBDatabase> {
  if (typeof indexedDB === 'undefined') {
    return Promise.reject(new Error('IndexedDB unavailable'));
  }
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(ASSET_CACHE_DB, 1);
    request.onerror = () => {
      dbPromise = null;
      reject(request.error);
    };
    request.onsuccess = () => {
      const db = request.result;
      db.onversionchange = () => {
        db.close();
        dbPromise = null;
      };
      resolve(db);
    };
    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(ASSET_CACHE_STORE)) {
        db.createObjectStore(ASSET_CACHE_STORE, { keyPath: 'assetUri' });
      }
    };
  });
  return dbPromise;
}

async function getCachedAssetBlob(assetUri: string): Promise<Blob | null> {
  try {
    const db = await openAssetCacheDB();
    return await new Promise<Blob | null>((resolve) => {
      const tx = db.transaction([ASSET_CACHE_STORE], 'readonly');
      const store = tx.objectStore(ASSET_CACHE_STORE);
      const req = store.get(assetUri);
      req.onsuccess = () => {
        const record = req.result as CachedAssetRecord | undefined;
        if (record && record.blob instanceof Blob) {
          resolve(record.blob);
        } else {
          resolve(null);
        }
      };
      req.onerror = () => resolve(null);
    });
  } catch {
    return null;
  }
}

async function setCachedAssetBlob(assetUri: string, blob: Blob, mimeType: string): Promise<void> {
  try {
    const db = await openAssetCacheDB();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction([ASSET_CACHE_STORE], 'readwrite');
      const store = tx.objectStore(ASSET_CACHE_STORE);
      const record: CachedAssetRecord = {
        assetUri,
        blob,
        mimeType,
        cachedAt: Date.now(),
      };
      const req = store.put(record);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  } catch {
    // Non-fatal if IDB write fails (e.g. private browsing or quota limit)
  }
}

/** Clear the in-memory blob URL cache (useful for tests and memory management). */
export function clearAssetBlobCache(): void {
  for (const url of resolvedBlobUrlCache.values()) {
    if (typeof URL !== 'undefined' && URL.revokeObjectURL && url.startsWith('blob:')) {
      try {
        URL.revokeObjectURL(url);
      } catch {
        // ignore
      }
    }
  }
  resolvedBlobUrlCache.clear();
  inFlightResolutions.clear();
}

/**
 * Encrypt a binary file/buffer and upload it to the workspace's storage node.
 * Returns the canonical `yada-asset://<workspaceId>/<sha256>` URI.
 */
export async function uploadEncryptedAsset(
  workspaceId: string,
  data: ArrayBuffer | Uint8Array,
  options: UploadAssetOptions = {}
): Promise<string> {
  const rawBytes = data instanceof Uint8Array ? data : new Uint8Array(data);
  const seed = await getWorkspaceSeed(workspaceId);
  const epoch = getCurrentEpoch();
  const epochKey = await deriveEpochKey(workspaceId, seed, epoch.epoch);

  const envelope = await sealEpochEnvelope(rawBytes, { workspaceId, kind: 'asset' }, {
    epoch: epoch.epoch,
    key: epochKey.key,
    header: { mime_type: options.mimeType, content: 'encrypted-asset-v1' },
  });

  // Compute SHA-256 hash of the encrypted envelope for content-addressing
  const hashBuffer = await crypto.subtle.digest('SHA-256', envelope as unknown as BufferSource);
  const assetId = uint8ArrayToHex(new Uint8Array(hashBuffer));

  await uploadAsset(workspaceId, assetId, envelope, options.mimeType);

  const assetUri = buildAssetUri(workspaceId, assetId);

  // Pre-seed local cache with the raw input data as a blob URL so the local device renders it immediately
  const mime = options.mimeType || 'application/octet-stream';
  const blob = new Blob([rawBytes as unknown as BlobPart], { type: mime });
  const localBlobUrl = URL.createObjectURL(blob);
  resolvedBlobUrlCache.set(assetUri, localBlobUrl);

  // Persist into IndexedDB cache asynchronously
  setCachedAssetBlob(assetUri, blob, mime).catch(() => {});

  return assetUri;
}

/**
 * Given a `yada-asset://<ws>/<id>` URI, fetches and decrypts the asset, returning
 * an object URL (`blob:...`) suitable for `<img src="...">`.
 *
 * Automatically caches decrypted objects in memory and IndexedDB.
 */
export async function resolveAssetBlobUrl(assetUri: string): Promise<string> {
  if (!isAssetUri(assetUri)) {
    return assetUri; // Pass-through for regular https: or data: URLs
  }

  const cached = resolvedBlobUrlCache.get(assetUri);
  if (cached) return cached;

  const inFlight = inFlightResolutions.get(assetUri);
  if (inFlight) return inFlight;

  const resolutionPromise = (async () => {
    try {
      // 1. Try reading from persistent IndexedDB cache
      const idbBlob = await getCachedAssetBlob(assetUri);
      if (idbBlob) {
        const objectUrl = URL.createObjectURL(idbBlob);
        resolvedBlobUrlCache.set(assetUri, objectUrl);
        return objectUrl;
      }

      // 2. Fetch from storage node if not in cache
      const parsed = parseAssetUri(assetUri);
      if (!parsed) throw new Error(`Invalid asset URI: ${assetUri}`);
      const { workspaceId, assetId } = parsed;

      const { data: envelopeBytes, mimeType: fetchedMimeType } = await fetchAsset(workspaceId, assetId);
      let decryptedBytes: Uint8Array;
      let mimeType = fetchedMimeType || 'application/octet-stream';

      // The asset id is the SHA-256 of the envelope as uploaded, so any other bytes are a substitution.
      const fetchedHash = uint8ArrayToHex(new Uint8Array(await crypto.subtle.digest('SHA-256', envelopeBytes as unknown as BufferSource)));
      if (fetchedHash !== assetId.toLowerCase()) {
        throw new EnvelopeIntegrityError(`Asset ${assetId} in workspace ${workspaceId} does not match its content hash`);
      }
      const seed = await getWorkspaceSeed(workspaceId);
      const { plaintext, header } = await openEpochEnvelope(
        envelopeBytes,
        { workspaceId, kind: 'asset' },
        async (epoch) => (await deriveEpochKey(workspaceId, seed, epoch)).key
      );
      decryptedBytes = plaintext;
      if (header.mime_type) {
        mimeType = header.mime_type;
      }

      const blob = new Blob([decryptedBytes as unknown as BlobPart], { type: mimeType });
      const objectUrl = URL.createObjectURL(blob);
      resolvedBlobUrlCache.set(assetUri, objectUrl);

      // Persist decrypted blob to IndexedDB
      setCachedAssetBlob(assetUri, blob, mimeType).catch(() => {});

      return objectUrl;
    } finally {
      inFlightResolutions.delete(assetUri);
    }
  })();

  inFlightResolutions.set(assetUri, resolutionPromise);
  return resolutionPromise;
}

if (typeof globalThis !== 'undefined') {
  (globalThis as Record<string, unknown>).__yadaResolveAssetBlobUrl = resolveAssetBlobUrl;
}

